// ErrorBoundary — top-level catch for uncaught render errors on the site.
//
// React error boundaries only exist as class components, so this stays a
// class. The handler logs to `console.error` (devtools + any future
// client-side telemetry) and forwards to Sentry with the React component
// stack attached, then renders a reload-centric fallback. Sentry is a
// no-op when `VITE_SENTRY_DSN` is unset — see lib/sentry.ts.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { Sentry } from "../lib/sentry";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  info: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("site error boundary caught", {
      message: error.message,
      stack: error.stack,
      componentStack: info.componentStack,
    });
    // Forward to Sentry with the React component stack attached as a
    // context. Sentry's own `react` context slot renders the stack in a
    // dedicated card on the issue page — much more useful than burying it
    // inside the exception message.
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack ?? "" },
      },
      tags: { boundary: "site-root" },
    });
    this.setState({ info });
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-paper dark:bg-ink">
        <div className="max-w-lg w-full rounded-lg border border-margin dark:border-paper/10 p-8">
          <h1 className="text-xl font-display text-ink dark:text-paper mb-2">
            Something went wrong
          </h1>
          <p className="text-sm text-ink/70 dark:text-paper/70 mb-6">
            The page ran into an unexpected error. Reloading usually fixes it.
            If the problem persists, email{" "}
            <a
              className="underline hover:text-ink dark:hover:text-paper"
              href="mailto:support@minutapp.tech"
            >
              support@minutapp.tech
            </a>
            .
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="rounded bg-accent px-4 py-2 text-sm text-paper hover:opacity-90 transition-opacity"
          >
            Reload
          </button>
          <details className="mt-6">
            <summary className="text-xs text-ink/50 dark:text-paper/50 cursor-pointer select-none">
              Technical details
            </summary>
            <pre className="mt-2 text-xs text-ink/60 dark:text-paper/60 whitespace-pre-wrap font-mono break-words">
              {error.message}
              {info?.componentStack ?? ""}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
